import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, SUPABASE_PUBLISHABLE_KEY } from '../lib/supabase';
import { fetchWithTimeout, bookSearchHeaders } from '../lib/bookSearch';
import { getCurrentUser } from '../lib/auth';
import { getPurchaseLinks } from '../hooks/useLibrary';
import { bookGenreNames } from '../lib/genreTaxonomy';

import { onCoverError } from '../lib/coverFallback';
function QuarterStarRating({ rating, onChange }) {
  const [hoverRating, setHoverRating] = useState(null);
  const display = hoverRating ?? rating ?? 0;

  function getValueFromEvent(e, starIndex) {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    const raw = starIndex + pct;
    return Math.round(raw * 4) / 4;
  }

  function handleMouseMove(e, starIndex) { setHoverRating(getValueFromEvent(e, starIndex)); }
  function handleClick(e, starIndex) {
    const value = getValueFromEvent(e, starIndex);
    onChange(value === rating ? null : value);
  }

  function getFill(slot) {
    const filled = display - (slot - 1);
    if (filled <= 0) return 0;
    if (filled >= 1) return 1;
    return filled;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)' }}>
      {[1, 2, 3, 4, 5].map(slot => {
        const fill = getFill(slot);
        return (
          <div key={slot} style={{ position: 'relative', width: 36, height: 36, cursor: 'pointer', flexShrink: 0 }}
            onMouseMove={e => handleMouseMove(e, slot - 1)}
            onMouseLeave={() => setHoverRating(null)}
            onClick={e => handleClick(e, slot - 1)}>
            <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-h2)', color: 'var(--neutral-300)', userSelect: 'none' }}>★</span>
            {fill > 0 && (
              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-h2)', color: '#f59e0b', userSelect: 'none', clipPath: `inset(0 ${Math.round((1 - fill) * 100)}% 0 0)` }}>★</span>
            )}
          </div>
        );
      })}
      <span style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: display > 0 ? 'var(--feedback-warning)' : 'var(--neutral-400)', marginLeft: 'var(--space-6)', minWidth: 32 }}>
        {display > 0 ? display.toFixed(2).replace(/\.?0+$/, '') : ''}
      </span>
    </div>
  );
}

export default function BookDetail() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [bookSource, setBookSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userLibraryEntry, setUserLibraryEntry] = useState(null);
  const [purchaseLinks, setPurchaseLinks] = useState([]);
  const [isInLibrary, setIsInLibrary] = useState(false);
  const [customShelves, setCustomShelves] = useState([]);
  const [isPending, setIsPending] = useState(false);

  const [shelf, setShelf] = useState('tbr');
  const [customShelfId, setCustomShelfId] = useState(null);
  const [rating, setRating] = useState(null);
  const [review, setReview] = useState('');
  const [dateStarted, setDateStarted] = useState('');
  const [dateRead, setDateRead] = useState('');
  const [saveStatus, setSaveStatus] = useState('');

  // Puts a user_library row on screen. Used by the page load, and by an Add
  // that finds the book already saved (from another tab, say).
  const showLibraryRow = (row) => {
    setIsInLibrary(true);
    setUserLibraryEntry(row);
    setShelf(row.shelf || 'tbr');
    setCustomShelfId(row.custom_shelf_id);
    setRating(row.rating);
    setReview(row.review || '');
    setDateStarted(row.date_started ? new Date(row.date_started).toISOString().split('T')[0] : '');
    setDateRead(row.date_read ? new Date(row.date_read).toISOString().split('T')[0] : '');
  };

  // The column that holds this book's id in user_library. csv_book_id is a
  // uuid (20260811000000_live_schema_snapshot.sql:3054), so it takes the id
  // from the URL as it is: parseInt of a uuid sent null or its leading digits,
  // and Postgres refused every save of a CSV book.
  const libraryRef = (source) => source === 'platform'
    ? { book_id: parseInt(bookId), csv_book_id: null }
    : { book_id: null, csv_book_id: bookId };

  // The reader's row for this book. UNIQUE (user_id, book_id) covers platform
  // books only; a CSV book's book_id is null, so nothing stops a second row
  // (older importers wrote csv_book_id). The oldest row is taken, because
  // .maybeSingle() errors on two and the page would show the book as not saved.
  const readLibraryRow = (userId, source) => {
    const query = supabase.from('user_library').select('*').eq('user_id', userId);
    return (source === 'platform' ? query.eq('book_id', bookId) : query.eq('csv_book_id', bookId))
      .order('id').limit(1).maybeSingle();
  };

  useEffect(() => {
    async function fetchBookDetails() {
      setLoading(true);

      const { data: platformBook } = await supabase
        .from('books')
        .select(`
          id, title, author_name, cover_image_url, blurb, description, description_source, avg_rating, publication_year, status,
          book_tags ( tag_type, tropes (canonical_name, category) ),
          book_genres ( id, genre:genres ( name ) ),
          book_subgenres ( id, subgenre:subgenres ( name ) )
        `)
        .eq('id', bookId)
        .maybeSingle();

      let bookData = null;
      let source = null;

      if (platformBook) {
        source = 'platform';
        const pending = platformBook.status === 'pending';
        setIsPending(pending);

        // Group tags by type from book_tags
        const tagsByType = {};
        (platformBook.book_tags || []).forEach(bt => {
          const type = bt.tag_type || 'trope';
          const name = bt.tropes?.canonical_name;
          if (!name) return;
          if (!tagsByType[type]) tagsByType[type] = [];
          tagsByType[type].push(name);
        });

        // Ticket 18. `description` is the write-up that actually matters: it is
        // what gets embedded, what the swipe card reads (MatchMeCard.jsx:50,
        // :564) and what BookDetailModal reads. This page was rendering
        // only `blurb` -- imported marketing copy -- so the same book read one
        // way on the card and another way on its own page.
        //
        // One column, either author: 20260904040000_description_provenance.sql
        // keeps whichever description currently wins in `description` and says
        // whose it is in `description_source`, with the superseded reader copy
        // archived in `reader_description`. So reading `description` is already
        // "the author's words, or the reader's if no author has claimed it" --
        // there is no second column to consult here, and that migration's own
        // three-state table is explicit that a reader-written description is
        // meant to give the book page copy.
        const description = platformBook.description;
        let blurb = platformBook.blurb;
        let tropes = tagsByType['trope'] || [];
        let moods = tagsByType['tone'] || [];
        let spice = tagsByType['spice'] || [];
        // From book_genres / book_subgenres: since PR 5 nothing writes a
        // book_tags genre row, so a new book had none here and showed no
        // genre on its own page. tagsByType['genre'] is only the fallback for
        // a book with no row in either new table. See bookGenreNames.
        let genres = bookGenreNames(platformBook, tagsByType['genre']);

        // If pending book has no write-up or no tags — check book_submissions
        // for fallback data. The write-up half of this test is now "no text at
        // all", not "no blurb": a book that already carries a description does
        // not need a blurb dug out of its submission row, because the
        // description is what the page will show. The tags half is unchanged
        // and still runs on its own, so a described book with no tags still
        // gets them from here.
        if (pending && ((!blurb && !description) || tropes.length === 0)) {
          const currentUser = await getCurrentUser();
          const { data: submission } = await supabase
            .from('book_submissions')
            .select('blurb, selected_tags, genres')
            .eq('submitted_by', currentUser?.id)
            .ilike('title', platformBook.title)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (submission) {
            if (!blurb && submission.blurb) blurb = submission.blurb;
            if (tropes.length === 0 && submission.selected_tags?.length > 0) {
              tropes = submission.selected_tags;
            }
            if (genres.length === 0 && submission.genres?.length > 0) {
              genres = submission.genres;
            }
          }
        }

        // If still no blurb — ask the book-search function, which tries Open
        // Library and falls back to Google Books. Going straight to Open
        // Library from here meant no blurb at all whenever they were down, and
        // Open Library's ISBN records frequently carry no description.
        //
        // Same widening as the submissions lookup above: this now fires only
        // when there is no text at all. Going out to Open Library and Google
        // Books for a book the author has already described spends a network
        // round trip and a write on copy this page will not render.
        if (!blurb && !description && pending) {
          try {
            const isbn = platformBook.isbn13 || platformBook.isbn;
            const { data: { session } } = await supabase.auth.getSession();
            const res = await fetchWithTimeout(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/book-search`,
              {
                method: 'POST',
                headers: bookSearchHeaders(session, SUPABASE_PUBLISHABLE_KEY),
                body: JSON.stringify(
                  isbn
                    ? { isbn }
                    : { title: platformBook.title, author: platformBook.author_name }
                ),
              }
            );
            if (res.ok) {
              const data = await res.json();
              blurb = data.book?.blurb || null;
              // Write it back so the next reader does not pay for the lookup.
              if (blurb) {
                const { error } = await supabase.from('books').update({ blurb }).eq('id', bookId);
                if (error) console.warn('Blurb write-back failed:', error.message);
              }
            }
          } catch (err) {
            console.warn('Could not fetch blurb:', err.message);
          }
        }

        bookData = {
          ...platformBook,
          blurb,
          // The description when there is one, the old blurb when there is
          // not. The blurb is NOT dropped on purpose: a large part of the
          // catalogue was imported with a blurb and has no description yet --
          // that backlog is exactly what the author backfill (ticket 51)
          // exists to clear. Switching outright would blank the write-up on
          // every one of those books today, which is a worse page than the
          // inconsistency this fixes. Each one heals by itself the moment its
          // author writes a description.
          //
          // .trim() because a description that is only whitespace should fall
          // through to the blurb rather than render an empty section under a
          // heading. `||` already handles NULL and the empty string.
          about: (description || '').trim() || blurb || null,
          // Whether the text `about` ended up holding was written by a reader,
          // so the page can say so rather than letting a visitor read another
          // reader's recommendation as the author's own words about their book.
          //
          // Deliberately narrow, and each half of the test earns its place:
          //   - only when the description is what won. If we fell through to
          //     the blurb, that is imported marketing copy and no reader wrote
          //     it, so the line would be a lie.
          //   - only on the literal string 'reader'. A NULL source means
          //     nobody recorded who wrote it -- rows predating
          //     20260904040000_description_provenance.sql are exactly that --
          //     and "we do not know" must not render as "a reader did". Saying
          //     nothing is the honest default; the line is a claim.
          //
          // A reader's words get here through the pitch in the "If You Like
          // What I Like" block (ProfileBlocks.jsx:1787), or an admin typing one
          // in on the approval queue (AdminPortal.jsx:431) or Health. All go
          // through set_reader_description(), which promotes into
          // books.description ONLY when the book has none, so this line can
          // never appear over an author's own words.
          aboutIsReaderWritten:
            !!(description || '').trim() && platformBook.description_source === 'reader',
          tropes,
          moods,
          spice,
          genres,
        };
      } else {
        const { data: csvBook } = await supabase
          .from('csv_book_imports')
          .select('*')
          .eq('id', bookId)
          .maybeSingle();

        if (csvBook) {
          source = 'csv';
          let tropes = [];
          if (csvBook.trope_ids?.length > 0) {
            const { data: tropesData } = await supabase
              .from('tropes').select('canonical_name').in('id', csvBook.trope_ids);
            tropes = tropesData?.map(t => t.canonical_name) || [];
          }
          bookData = {
            id: csvBook.id, title: csvBook.title, author_name: csvBook.author_name,
            cover_image_url: csvBook.cover_image_url, blurb: csvBook.blurb,
            // csv_book_imports has no description column at all -- checked
            // against the table definition in the schema snapshot, it carries
            // `blurb` and nothing else of this kind. So an import's write-up
            // is its blurb, and `about` says so rather than reading undefined.
            about: csvBook.blurb || null,
            avg_rating: csvBook.avg_rating, publication_year: csvBook.publication_year,
            tropes, moods: [], spice: [], genres: [],
          };
        }
      }

      if (!bookData) { setLoading(false); return; }

      setBook(bookData);
      setBookSource(source);

      const user = await getCurrentUser();
      if (user) {
        const { data: libraryData, error: libraryError } = await readLibraryRow(user.id, source);
        if (libraryError) console.error('Error loading the library row:', libraryError);
        if (libraryData) showLibraryRow(libraryData);
      }

      if (source === 'platform') {
        const links = await getPurchaseLinks(bookId);
        setPurchaseLinks(links);
      }

      setLoading(false);
    }
    fetchBookDetails();
  }, [bookId]);

  useEffect(() => {
    async function fetchCustomShelves() {
      const user = await getCurrentUser();
      if (!user) return;
      const { data } = await supabase.from('custom_shelves').select('id, name, emoji, color').eq('user_id', user.id);
      setCustomShelves(data || []);
    }
    fetchCustomShelves();
  }, []);

  const autoSave = async (updates) => {
    const user = await getCurrentUser();
    if (!user) return;
    setSaveStatus('saving');

    const fullUpdates = {
      user_id: user.id, ...libraryRef(bookSource), shelf, custom_shelf_id: customShelfId,
      rating: rating || null, review: review || null,
      date_started: dateStarted || null, date_read: dateRead || null,
      date_added: userLibraryEntry?.date_added || new Date().toISOString(),
      ...updates,
    };

    let data, error;
    if (userLibraryEntry?.id) {
      ({ data, error } = await supabase.from('user_library').update(fullUpdates).eq('id', userLibraryEntry.id).select());
    } else {
      ({ data, error } = await supabase.from('user_library').insert(fullUpdates).select());
    }

    if (error) { console.error('Error saving:', error); setSaveStatus(''); }
    else {
      if (data?.[0]) setUserLibraryEntry(data[0]);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 2000);
    }
  };

  const handleShelfChange = async (newShelf) => {
    setShelf(newShelf);
    setCustomShelfId(null);
    const updates = { shelf: newShelf, custom_shelf_id: null };
    if (newShelf === 'reading' && !dateStarted) {
      const today = new Date().toISOString().split('T')[0];
      updates.date_started = today; setDateStarted(today);
    }
    if (newShelf === 'read' && !dateRead) {
      const today = new Date().toISOString().split('T')[0];
      updates.date_read = today; setDateRead(today);
      if (!dateStarted) { updates.date_started = today; setDateStarted(today); }
    }
    if (newShelf === 'loved') {
      const today = new Date().toISOString().split('T')[0];
      if (!dateRead) { updates.date_read = today; setDateRead(today); }
      if (!dateStarted) { updates.date_started = today; setDateStarted(today); }
    }
    await autoSave(updates);
  };

  const handleCustomShelfChange = async (shelfId) => {
    setCustomShelfId(shelfId);
    await autoSave({ custom_shelf_id: shelfId });
  };

  const handleRatingChange = async (newRating) => {
    setRating(newRating);
    await autoSave({ rating: newRating });
  };

  useEffect(() => {
    if (!isInLibrary) return;
    const timeoutId = setTimeout(() => { autoSave({ review: review || null }); }, 1000);
    return () => clearTimeout(timeoutId);
  }, [review]);

  const handleAddToLibrary = async () => {
    const user = await getCurrentUser();
    if (!user) { alert('Please log in to add books'); return; }
    // Saved since this page loaded (from another tab, say)? Show that row. A
    // CSV book has no unique key to refuse a second row, so look first; a
    // platform book's second insert is refused with 23505, handled below.
    if (bookSource !== 'platform') {
      const { data: saved, error: readError } = await readLibraryRow(user.id, bookSource);
      if (saved) { showLibraryRow(saved); return; }
      if (readError) {
        console.error('Error reading the saved library row:', readError);
        alert('Could not save that book. Please try again.');
        return;
      }
    }
    // Keep the row: autoSave updates by its id and Remove deletes by it.
    // Without it, the first shelf or rating change after Add inserted a
    // second row, which UNIQUE (user_id, book_id) refuses, so the change only
    // lived on screen; and Remove on the same visit threw on a null id.
    const { data, error } = await supabase.from('user_library').insert({
      user_id: user.id, ...libraryRef(bookSource), shelf: 'tbr', date_added: new Date().toISOString()
    }).select().single();
    if (error?.code === '23505') {
      const { data: saved, error: readError } = await readLibraryRow(user.id, bookSource);
      if (saved) { showLibraryRow(saved); return; }
      console.error('Error reading the saved library row:', readError);
    }
    if (error) {
      console.error('Error adding to library:', error);
      alert('Could not save that book. Please try again.');
      return;
    }
    setIsInLibrary(true); setUserLibraryEntry(data); setShelf('tbr');
  };

  // Stays on this page, which goes back to "Add to Library". It used to send
  // the reader to /library, which no longer exists (the shelves live in the
  // Sticky Dock), so they only reached their profile through the catch-all.
  const handleRemoveFromLibrary = async () => {
    if (!confirm('Remove this book from your library?')) return;
    const user = await getCurrentUser();
    if (!user) return;
    // .select('id') tells "deleted" from "deleted nothing", which is not an error.
    const { data, error } = userLibraryEntry?.id
      ? await supabase.from('user_library').delete().eq('id', userLibraryEntry.id).select('id')
      : { data: null, error: new Error('no library row on this page') };
    if (error || !data?.length) {
      console.error('Error removing from library:', error || 'no row deleted');
      alert('Could not save your changes. Please try again.');
      return;
    }
    // Clear what the row held too, or the next Add's first save would write
    // the old rating and review straight back.
    setIsInLibrary(false); setUserLibraryEntry(null);
    setShelf('tbr'); setCustomShelfId(null); setRating(null); setReview(''); setDateStarted(''); setDateRead('');
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--brand-paper)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="animate-spin w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!book) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--brand-paper)', padding: '60px 20px', textAlign: 'center' }}>
        <p className="text-stone-600">Book not found</p>
        <button onClick={() => navigate(-1)} className="mt-4 px-6 py-2 bg-amber-600 text-white rounded-lg">Go Back</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--brand-paper)', paddingBottom: 60 }}>
      <style>{`
        @media (max-width: 1024px) {
          .book-detail-header > div { padding: 0 30px !important; }
          .book-detail-container { padding: 0 30px !important; }
          .book-detail-grid { grid-template-columns: 200px 1fr !important; gap: 24px !important; }
        }
        @media (max-width: 768px) {
          .book-detail-header { padding: 20px 0 !important; }
          .book-detail-header > div { padding: 0 20px !important; }
          .book-detail-container { padding: 0 20px !important; }
          .book-detail-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
          .book-cover-section { max-width: 250px !important; margin: 0 auto !important; }
          .book-title { font-size: 28px !important; }
          .status-grid { grid-template-columns: repeat(3, 1fr) !important; gap: 8px !important; }
        }
      `}</style>

      <div className="book-detail-header" style={{ background: 'linear-gradient(180deg, var(--neutral-800), var(--neutral-700))', padding: '24px 0', marginBottom: 'var(--space-h3)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 60px' }}>
          <button onClick={() => navigate(-1)} className="text-stone-300 hover:text-white text-sm mb-4 flex items-center gap-2">← Back</button>
          <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
            {bookSource === 'csv' && (
              <div className="inline-flex items-center gap-2 bg-amber-600/20 border border-amber-600/30 px-3 py-1 rounded-lg">
                <span className="text-xs font-semibold text-amber-200">📥 Imported from CSV</span>
              </div>
            )}
            {isPending && (
              <div className="inline-flex items-center gap-2 bg-stone-600/20 border border-stone-500/30 px-3 py-1 rounded-lg">
                <span className="text-xs font-semibold text-stone-300">⏳ Pending Review, not yet live on platform</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="book-detail-container" style={{ maxWidth: 1000, margin: '0 auto', padding: '0 60px' }}>
        <div className="book-detail-grid" style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 'var(--space-h4)', alignItems: 'start' }}>

          {/* Left: Cover */}
          <div className="book-cover-section">
            <div style={{ width: '100%', aspectRatio: '2/3', borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', background: book.cover_image_url ? 'none' : 'linear-gradient(135deg, #9E4B2C, var(--feedback-warning))' }}>
              {book.cover_image_url
                ? <img src={book.cover_image_url} alt={book.title} className="w-full h-full object-cover" onError={onCoverError} />
                : <div className="w-full h-full flex items-center justify-center text-white text-6xl">📚</div>
              }
            </div>

            {bookSource === 'platform' && purchaseLinks.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-stone-700 mb-3">Buy this book</h3>
                <div className="flex flex-col gap-2">
                  {purchaseLinks.map((link, i) => (
                    <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                      className="px-4 py-2 bg-amber-100 text-amber-800 rounded-lg text-sm font-semibold hover:bg-amber-200 transition-colors text-center">
                      {link.retailer} ↗
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Info */}
          <div>
            <h1 className="book-title text-4xl font-bold text-stone-800 mb-2" style={{ fontFamily: 'var(--font-display)' }}>{book.title}</h1>
            <p className="text-xl text-stone-600 mb-4">by {book.author_name}</p>
            {book.publication_year && <p className="text-sm text-stone-500 mb-4">Published {book.publication_year}</p>}

            {/* Tropes / Tags */}
            {book.tropes?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Tropes</p>
                <div className="flex flex-wrap gap-2">
                  {book.tropes.map((trope, i) => (
                    <span key={i} style={{ padding: '5px 12px', background: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-ink)', borderRadius: 'var(--radius-xxl)', fontSize: 'var(--font-size-base)', fontWeight: 500, border: '1px solid var(--feedback-warning-border)' }}>{trope}</span>
                  ))}
                  {isPending && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontStyle: 'italic', alignSelf: 'center' }}>· pending review</span>}
                </div>
              </div>
            )}

            {/* Mood */}
            {book.moods?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Mood</p>
                <div className="flex flex-wrap gap-2">
                  {book.moods.map((m, i) => (
                    <span key={i} style={{ padding: '4px 12px', background: 'var(--neutral-800)', color: 'var(--brand-paper)', borderRadius: 14, fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>{m}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Spice */}
            {book.spice?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Spice</p>
                <span style={{ padding: '4px 12px', background: '#fef2f2', color: '#b91c1c', borderRadius: 14, fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>{book.spice[0]}</span>
              </div>
            )}

            {/* Genres */}
            {book.genres?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Genre</p>
                <div className="flex flex-wrap gap-2">
                  {book.genres.map((g, i) => (
                    <span key={i} style={{ padding: '4px 12px', background: 'var(--feedback-success-bg)', color: '#166534', borderRadius: 14, fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>{g}</span>
                  ))}
                </div>
              </div>
            )}

            {book.about && (
              <div className="mb-6 mt-4">
                <h3 className="text-sm font-semibold text-stone-700 mb-2">About this book</h3>
                {/* Sits above the text, not below it, so it is read before the
                    words rather than as a correction afterwards.

                    stone-600, not the stone-500 a caption would normally take.
                    This page's background is --brand-paper (#faf8f5), not
                    white, and at text-xs (12px, normal weight) WCAG AA wants
                    4.5:1: stone-500 lands on 4.53:1 there, which clears it by
                    0.03 and would stop clearing it if the paper colour ever
                    moved a shade. stone-600 is 7.20:1 on the same background.
                    Computed, not estimated. Size alone already separates this
                    line from the body copy. */}
                {book.aboutIsReaderWritten && (
                  <p className="text-xs text-stone-600 mb-2">Written by a reader, not the author.</p>
                )}
                <p className="text-stone-600 leading-relaxed whitespace-pre-line">{book.about}</p>
              </div>
            )}

            <div className="border-t border-stone-200 my-8"></div>

            {!isInLibrary ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                <p className="text-stone-700 mb-4">Add this book to your library to track your reading</p>
                <button onClick={handleAddToLibrary} className="px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors">
                  📚 Add to Library
                </button>
              </div>
            ) : (
              <div className="bg-white border border-stone-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-stone-800">Track Your Progress</h3>
                  {saveStatus === 'saving' && <span className="text-xs text-stone-400">Saving...</span>}
                  {saveStatus === 'saved' && <span className="text-xs text-emerald-600">✓ Saved</span>}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-stone-700 mb-3">Reading Status</label>
                  <div className="status-grid grid grid-cols-5 gap-2">
                    {[
                      { value: 'tbr', emoji: '📚', label: 'To Read' },
                      { value: 'reading', emoji: '📖', label: 'Reading' },
                      { value: 'read', emoji: '✅', label: 'Finished' },
                      { value: 'loved', emoji: '❤️', label: 'Loved' },
                      { value: 'dnf', emoji: '⛔', label: 'DNF' },
                    ].map(option => (
                      <button key={option.value} onClick={() => handleShelfChange(option.value)}
                        className={`status-button p-3 rounded-lg border-2 transition-all text-center ${shelf === option.value && !customShelfId ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-amber-300'}`}>
                        <div className="text-2xl mb-1">{option.emoji}</div>
                        <div className="text-xs font-medium text-stone-700">{option.label}</div>
                      </button>
                    ))}
                  </div>
                  {dateStarted && <p className="text-xs text-stone-500 mt-2">Started: {new Date(dateStarted).toLocaleDateString()}</p>}
                  {dateRead && <p className="text-xs text-stone-500">Finished: {new Date(dateRead).toLocaleDateString()}</p>}
                </div>

                {customShelves.length > 0 && (
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-stone-700 mb-3">Or add to custom shelf</label>
                    <div className="custom-shelf-grid grid grid-cols-3 gap-2">
                      {customShelves.map(customShelf => (
                        <button key={customShelf.id} onClick={() => handleCustomShelfChange(customShelf.id)}
                          className={`p-3 rounded-lg border-2 transition-all text-left ${customShelfId === customShelf.id ? 'border-amber-500' : 'border-stone-200 hover:border-amber-300'}`}
                          style={{ background: customShelfId === customShelf.id ? `linear-gradient(135deg, ${customShelf.color}20, ${customShelf.color}10)` : 'white' }}>
                          <div className="text-xl mb-1">{customShelf.emoji}</div>
                          <div className="text-xs font-medium text-stone-700">{customShelf.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-stone-700 mb-3">Your Rating</label>
                  <QuarterStarRating rating={rating} onChange={handleRatingChange} />
                  <p className="text-xs text-stone-400 mt-2">Hover over stars to set ¼ or ½ star ratings · Click the same rating to clear</p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-stone-700 mb-2">My Thoughts <span className="text-stone-400 font-normal">(optional)</span></label>
                  <textarea value={review} onChange={e => setReview(e.target.value)} placeholder="What did you think?" rows={4}
                    className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none text-sm" />
                  <p className="text-xs text-stone-400 mt-1">Auto-saves as you type</p>
                </div>

                <button onClick={handleRemoveFromLibrary} className="w-full px-6 py-2 bg-stone-100 text-stone-600 rounded-lg text-sm font-medium hover:bg-red-50 hover:text-red-600 transition-colors">
                  Remove from Library
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}